import { useCallback, useEffect, useState } from 'react';
import { apiService, updateAccountDetails } from '../services/api';
import {
  arrayBufferToBase64,
  deriveKey,
  type EncryptionParams,
  exportKey,
  generateIV,
  generateSalt,
  importMasterKey,
  restoreMasterKey,
  restoreMasterKeyFromRecovery,
  setupEncryption,
  setupIdentityKeys,
  wrapKey,
} from '../services/encryptionService';
import storageService from '../services/storageService';

export const useEncryptionKeys = (setAuthError: (error: string | null) => void) => {
  const [encryptionKey, setEncryptionKey] = useState<CryptoKey | null>(null);
  const [recoveryCode, setRecoveryCode] = useState<string | null>(null);
  const [isRecoveryNeeded, setIsRecoveryNeeded] = useState<boolean>(false);
  const [tempPassword, setTempPassword] = useState<string | null>(null);

  // Load encryption key from session storage on mount
  useEffect(() => {
    const storedKey = sessionStorage.getItem('encryption_key');
    if (storedKey && !encryptionKey) {
      importMasterKey(storedKey)
        .then((key) => {
          setEncryptionKey(key);
        })
        .catch((_err) => {
          // If it fails to import, clear the storage to avoid repeated errors
          sessionStorage.removeItem('encryption_key');
        });
    }
  }, [encryptionKey]);

  // Sync encryption key to storageService for non-React contexts
  useEffect(() => {
    storageService.setEncryptionKey(encryptionKey);
  }, [encryptionKey]);

  const recoverEncryption = useCallback(
    async (code: string, password: string): Promise<void> => {
      try {
        const details = await apiService.getAccountDetails();
        if (!details.success || !details.account?.recoveryParams) {
          throw new Error('Recovery not available for this account.');
        }

        const mdk = await restoreMasterKeyFromRecovery(
          code,
          details.account.recoveryParams as EncryptionParams
        );
        const salt = generateSalt();
        const iv = generateIV();
        const kek = await deriveKey(password, salt);
        const wrappedKeyBuffer = await wrapKey(mdk, kek, iv);

        const newParams: EncryptionParams = {
          salt: arrayBufferToBase64(salt),
          wrappedKey: arrayBufferToBase64(wrappedKeyBuffer),
          iv: arrayBufferToBase64(iv),
        };

        await updateAccountDetails({ encryptionParams: newParams });

        setEncryptionKey(mdk);
        sessionStorage.setItem('encryption_key', await exportKey(mdk));
        setIsRecoveryNeeded(false);
        setAuthError(null);
      } catch (_error) {
        throw new Error('Invalid recovery code or recovery failed.');
      }
    },
    [setAuthError]
  );

  const resetEncryptionKeys = useCallback(
    async (password: string): Promise<void> => {
      try {
        const setup = await setupEncryption(password);
        const identityKeys = await setupIdentityKeys(setup.mdk);

        setEncryptionKey(setup.mdk);
        sessionStorage.setItem('encryption_key', await exportKey(setup.mdk));
        setRecoveryCode(setup.recoveryCode);

        await updateAccountDetails({
          encryptionParams: setup.params,
          recoveryParams: setup.recoveryParams,
          publicKey: identityKeys.publicKey,
          encryptedPrivateKey: identityKeys.encryptedPrivateKey,
        });

        setIsRecoveryNeeded(false);
        setAuthError(null);
      } catch (_error) {
        throw new Error('Failed to reset encryption keys. Please contact support.');
      }
    },
    [setAuthError]
  );

  const handleEncryptionSetup = useCallback(
    async (password: string) => {
      try {
        const details = await apiService.getAccountDetails();
        if (details.success && details.account) {
          const subscription = details.account.subscription;
          if (subscription?.status === 'canceled') {
            return;
          }
          let periodEnd: Date | null = null;
          if (subscription?.current_period_end) {
            periodEnd =
              typeof subscription.current_period_end === 'string'
                ? new Date(subscription.current_period_end)
                : (subscription.current_period_end as unknown as Date);
          }
          if (periodEnd && periodEnd < new Date()) {
            return;
          }

          if (details.account.encryptionParams) {
            try {
              const key = await restoreMasterKey(
                password,
                details.account.encryptionParams as EncryptionParams
              );
              setEncryptionKey(key);
              sessionStorage.setItem('encryption_key', await exportKey(key));

              if (!details.account.publicKey) {
                const identityKeys = await setupIdentityKeys(key);
                await updateAccountDetails({
                  publicKey: identityKeys.publicKey,
                  encryptedPrivateKey: identityKeys.encryptedPrivateKey,
                });
              }
            } catch (_restoreError) {
              setIsRecoveryNeeded(true);
              setAuthError('Data unlock failed. Please provide your Recovery Code.');
            }
          } else {
            const setup = await setupEncryption(password);
            const identityKeys = await setupIdentityKeys(setup.mdk);

            setEncryptionKey(setup.mdk);
            sessionStorage.setItem('encryption_key', await exportKey(setup.mdk));
            setRecoveryCode(setup.recoveryCode);
            await updateAccountDetails({
              encryptionParams: setup.params,
              recoveryParams: setup.recoveryParams,
              publicKey: identityKeys.publicKey,
              encryptedPrivateKey: identityKeys.encryptedPrivateKey,
            });
          }
        }
      } catch (_error: unknown) {
        /* ignore */
      }
    },
    [setAuthError]
  );

  const clearEncryptionState = useCallback(() => {
    setEncryptionKey(null);
    sessionStorage.removeItem('encryption_key');
    setRecoveryCode(null);
    setTempPassword(null);
    setIsRecoveryNeeded(false);
  }, []);

  const clearRecoveryCode = useCallback(() => setRecoveryCode(null), []);

  const requireEncryptionRecovery = useCallback(() => {
    setIsRecoveryNeeded(true);
  }, []);

  return {
    encryptionKey,
    recoveryCode,
    clearRecoveryCode,
    isRecoveryNeeded,
    requireEncryptionRecovery,
    recoverEncryption,
    resetEncryptionKeys,
    handleEncryptionSetup,
    clearEncryptionState,
    tempPassword,
    setTempPassword,
  };
};
